/**
 * RustedBlockage — impassable obstacle shattered by Resonant Drop.
 *
 * Represents rusted chains, gears, and debris blocking a passage.
 * Visible to Aura Sight. Can only be destroyed by the co-op ability.
 *
 * Physics: has a static physics body to block players. On shatter,
 * the body is removed and a particle burst + fade animation plays.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Animation } from "@babylonjs/core/Animations/animation";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";

export interface RustedBlockageConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
}

export class RustedBlockage extends InteractiveObject {
  private shattered = false;
  private blockMesh: Mesh;
  private chainMeshes: Mesh[] = [];
  private blockAggregate: PhysicsAggregate;

  constructor(scene: Scene, config: RustedBlockageConfig) {
    const size = config.size ?? new Vector3(1.6, 4.8, 1.0);
    super(scene, { ...config, size });

    // Main block body
    this.blockMesh = MeshBuilder.CreateBox(
      `blockage_${config.key}_body`,
      { width: size.x, height: size.y, depth: size.z },
      scene,
    );
    this.blockMesh.parent = this.rootMesh;
    this.blockMesh.position = Vector3.Zero();

    const blockMat = new StandardMaterial(`blockage_${config.key}_mat`, scene);
    blockMat.diffuseColor = new Color3(0.54, 0.27, 0.07); // Rust brown
    blockMat.specularColor = new Color3(0.15, 0.1, 0.05);
    blockMat.emissiveColor = new Color3(0.02, 0.01, 0.005);
    this.blockMesh.material = blockMat;
    this.material = blockMat;

    // Decorative chain-link cylinders stacked vertically
    const chainCount = Math.floor(size.y / 0.6);
    for (let i = 0; i < chainCount; i++) {
      const offsetX = (i % 2 === 0 ? -0.2 : 0.2);
      const chain = MeshBuilder.CreateTorus(
        `blockage_${config.key}_chain_${i}`,
        { diameter: 0.5, thickness: 0.06, tessellation: 12 },
        scene,
      );
      chain.parent = this.rootMesh;
      chain.position = new Vector3(
        offsetX,
        -size.y / 2 + 0.3 + i * 0.6,
        0,
      );
      chain.rotation.y = i * 0.4;

      const chainMat = new StandardMaterial(
        `blockage_${config.key}_chain_${i}_mat`,
        scene,
      );
      chainMat.diffuseColor = new Color3(0.42, 0.2, 0.06);
      chainMat.specularColor = Color3.Black();
      chain.material = chainMat;
      this.chainMeshes.push(chain);
    }

    // Gear accents
    for (let g = 0; g < 2; g++) {
      const gear = MeshBuilder.CreateCylinder(
        `blockage_${config.key}_gear_${g}`,
        { diameter: 0.4, height: 0.1, tessellation: 8 },
        scene,
      );
      gear.parent = this.rootMesh;
      gear.position = new Vector3(0, g === 0 ? -size.y * 0.25 : size.y * 0.25, size.z * 0.5 + 0.02);
      gear.rotation.x = Math.PI / 2;

      const gMat = new StandardMaterial(`blockage_gear_${g}_mat`, scene);
      gMat.diffuseColor = new Color3(0.63, 0.32, 0.18);
      gMat.specularColor = Color3.Black();
      gear.material = gMat;
      this.chainMeshes.push(gear);
    }

    // Static physics body to block players
    this.blockAggregate = new PhysicsAggregate(
      this.blockMesh,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.5 },
      scene,
    );

    // Attempt to load 3D model override (falls back to procedural mesh)
    this.loadModelOverride("/assets/models/environment/rusted_blockage.glb");
  }

  /**
   * Shatter this blockage (called when Resonant Drop shockwave reaches it).
   */
  shatter(): void {
    if (this.shattered) return;
    this.shattered = true;
    this.state = "solved";

    // Disable physics so players can pass through
    this.blockAggregate.body.setMotionType(1); // DYNAMIC to detach
    this.blockAggregate.dispose();

    // Particle burst
    const particles = new ParticleSystem("blockageShatter", 40, this.scene);
    particles.createPointEmitter(
      new Vector3(-2, -2, -1),
      new Vector3(2, 2, 1),
    );
    particles.emitter = this.rootMesh.position.clone();
    particles.minLifeTime = 0.5;
    particles.maxLifeTime = 1.2;
    particles.minSize = 0.1;
    particles.maxSize = 0.3;
    particles.minEmitPower = 3;
    particles.maxEmitPower = 8;
    particles.color1 = new Color4(0.54, 0.27, 0.07, 1);
    particles.color2 = Color4.FromHexString(COLORS.COPPER + "ff");
    particles.colorDead = new Color4(0.2, 0.1, 0.03, 0);
    particles.gravity = new Vector3(0, -8, 0);
    particles.emitRate = 0;
    particles.manualEmitCount = 40;
    particles.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    particles.start();

    // Fade out all child meshes
    const fps = 30;
    const totalFrames = 15; // ~500ms
    const allMeshes = [this.blockMesh, ...this.chainMeshes];

    for (const mesh of allMeshes) {
      const fadeAnim = new Animation(
        `blockageFade_${mesh.name}`,
        "visibility",
        fps,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT,
      );
      fadeAnim.setKeys([
        { frame: 0, value: 1 },
        { frame: totalFrames, value: 0 },
      ]);
      mesh.animations = [fadeAnim];
      this.scene.beginAnimation(mesh, 0, totalFrames, false);
    }

    // Clean up particles after they finish
    setTimeout(() => {
      particles.stop();
      particles.dispose();
    }, 1500);

    gameEvents.emit(GameEvents.BLOCKAGE_SHATTERED, { key: this.key });
  }

  get isShattered(): boolean {
    return this.shattered;
  }
}
